import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { Link, useSearchParams } from 'react-router-dom';
import JsBarcode from 'jsbarcode';
import { QRCodeSVG } from 'qrcode.react';
import { dispatchApi } from '../../services/dispatchApi';
import { useApp } from '../../App';
import { EmptyState, LoadingState } from '../Common/PanelState';

const FLARE_RED = '#c4151c';
const DEFAULT_FROM_ADDRESS = 'Flat# 200/14, First Floor Tariq Block Market\nNew Garden Town Lahore.';
const DEFAULT_FROM_PHONE = 'Ph# 0321-4664602';
const DEFAULT_FOOTER = 'Flare Magazine, Lahore\nThank you for subscribing!';
const DEFAULT_MOBILE = '0333-4132462';

const Icon = ({ path, className = 'h-5 w-5' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
);

function StatCard({ label, value, hint, icon, iconBg }) {
  return (
    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-lg flex items-center justify-center text-white shadow-sm shrink-0"
          style={{ backgroundColor: iconBg }}
        >
          <Icon path={icon} className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase text-gray-500 tracking-wide">{label}</div>
          <div className="text-2xl font-extrabold text-gray-900 leading-tight">{value}</div>
          {hint ? <div className="text-xs text-gray-400 mt-0.5">{hint}</div> : null}
        </div>
      </div>
    </div>
  );
}

function formatDeskTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-PK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function pct(part, total) {
  const t = Number(total) || 0;
  if (t <= 0) return 0;
  return Math.min(100, Math.round((Number(part) / t) * 100));
}

function formatSlipMobile(p) {
  let d = String(p || '').replace(/\D/g, '');
  if (d.startsWith('92') && d.length >= 12) d = `0${d.slice(2)}`;
  if (/^03\d{9}$/.test(d)) return `${d.slice(0, 4)}-${d.slice(4)}`;
  return String(p || '').trim();
}

function slipPhones(slip) {
  const mobile = formatSlipMobile(slip?.phone);
  const ptcl = String(slip?.landline || slip?.ptcl || '').trim();
  return { mobile, ptcl };
}

function footerLines(raw) {
  const text = String(raw || DEFAULT_FOOTER).trim();
  const lines = text
    .split(/\n+/)
    .map((l) => l.replace(/^from:\s*/i, '').trim())
    .filter(Boolean);
  return lines.length ? lines : ['Flare Magazine, Lahore', 'Thank you for subscribing!'];
}

function fromAddressTwoLines(address) {
  const text = String(address || DEFAULT_FROM_ADDRESS).trim();
  if (!text) return ['Flat# 200/14, First Floor Tariq Block Market', 'New Garden Town Lahore.'];
  const nl = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  if (nl.length >= 2) return nl.slice(0, 2);
  const garden = text.match(/^(.*?)(?:,?\s*)(New\s+Garden Town.*)$/i);
  if (garden) return [garden[1].replace(/,\s*$/, '').trim(), garden[2].trim()];
  return [text];
}

function fromAddressOneLine(address) {
  return fromAddressTwoLines(address).join(', ');
}

function barcodeValue(slip) {
  return String(slip?.tracking_number || slip?.barcode || slip?.reference || '').trim();
}

function siteUrl(site) {
  const raw = String(site || 'digital.flare.pk').trim() || 'digital.flare.pk';
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw.replace(/^\/+/, '')}`;
}

function makeBarcodeSvg(value, { height = 28, width = 1.05, fontSize = 0 } = {}) {
  const code = String(value || '').trim();
  if (!code || typeof document === 'undefined') return '';
  try {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    JsBarcode(svg, code, {
      format: 'CODE128',
      displayValue: fontSize > 0,
      fontSize,
      height,
      margin: 0,
      width,
      background: 'transparent',
      lineColor: '#111827',
      font: 'Arial',
      textMargin: 0,
    });
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.style.maxWidth = '100%';
    svg.style.height = 'auto';
    svg.style.display = 'block';
    svg.style.margin = '0 auto';
    return svg.outerHTML;
  } catch {
    return '';
  }
}

function SlipBarcode({ value, height, barWidth, fontSize }) {
  const svg = useMemo(
    () => makeBarcodeSvg(value, { height, width: barWidth, fontSize }),
    [value, height, barWidth, fontSize],
  );
  if (!svg) return null;
  return (
    <div
      className="barcode-wrap"
      style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

function parseFromBlock(raw) {
  const fallback = { address: DEFAULT_FROM_ADDRESS, phone: DEFAULT_FROM_PHONE };
  const text = String(raw || `${DEFAULT_FROM_ADDRESS}\n${DEFAULT_FROM_PHONE}`).trim();
  if (!text) return fallback;

  const isPhoneLine = (line) => /(?:ph#?|phone|tel|mob)\b/i.test(line) || /^\+?\d[\d\s-]{6,}$/.test(line);
  const lines = text.split(/\n+/).map((l) => l.replace(/^from:\s*/i, '').trim()).filter(Boolean);
  let phone = '';
  const addrParts = [];

  lines.forEach((line) => {
    if (isPhoneLine(line)) {
      phone = line;
      return;
    }
    const split = line.match(/^(.*?)(?:\s+)(ph#?\s*[:.]?\s*\+?\d[\d\s-]+)$/i);
    if (split) {
      if (split[1].trim()) addrParts.push(split[1].trim());
      phone = split[2].trim();
      return;
    }
    addrParts.push(line);
  });

  return {
    address: addrParts.join('\n') || fallback.address,
    phone: phone || fallback.phone,
  };
}

function issueLine(slip) {
  const issue = String(slip?.issue || 'Flare Magazine').trim();
  const month = String(slip?.issue_month || '').trim();
  if (month && !issue.toLowerCase().includes(month.toLowerCase())) {
    return `${issue} \u00B7 ${month}`;
  }
  return issue || 'Flare Magazine';
}

function isFakeEmail(value) {
  const s = String(value || '').trim().toLowerCase();
  if (!s) return false;
  return s.endsWith('@example.com') || s.includes('complimentary.flare.pk') || s.endsWith('@mailinator.com') || s.endsWith('@test.com');
}

function displayEmail(value) {
  const s = String(value || '').trim();
  if (!s || isFakeEmail(s)) return '';
  return s;
}

function isWebsitePackItem(row) {
  const src = String(row?.source_type || '').toLowerCase();
  const label = String(row?.label_type || '').toLowerCase();
  return src === 'order' || label === 'order' || label === 'website order' || label === 'websiteorder';
}

const StatusPill = ({ status }) => {
  const map = {
    Queued: 'bg-amber-50 text-amber-800 border-amber-100',
    SlipPrinted: 'bg-sky-50 text-sky-800 border-sky-100',
    Shipped: 'bg-indigo-50 text-indigo-800 border-indigo-100',
    EmailSent: 'bg-emerald-50 text-emerald-800 border-emerald-100',
    NeedsRetry: 'bg-rose-50 text-rose-800 border-rose-100',
    Cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full border ${map[status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
      {status}
    </span>
  );
};

const LABEL_BADGE = {
  Complimentary: 'bg-indigo-50 text-indigo-800 border-indigo-100',
  'Paid Sub': 'bg-emerald-50 text-emerald-800 border-emerald-100',
  'Website Order': 'bg-sky-50 text-sky-800 border-sky-100',
  Slip: 'bg-slate-50 text-slate-800 border-slate-200',
};

const formatLabelType = (raw) => {
  const v = String(raw || '').trim();
  const lower = v.toLowerCase();
  if (lower === 'free' || lower === 'complimentary') return 'Complimentary';
  if (lower === 'paidsub' || lower === 'paid sub') return 'Paid Sub';
  if (lower === 'order' || lower === 'website order') return 'Website Order';
  return v || 'Slip';
};

const SLIP_SCALE = {
  sm: {
    logoH: 46, taglinePx: 9, padX: 12, padY: 10, bodyPadTop: 16, bodyPadBot: 8,
    logoMaxPct: '58%', sideMin: 92, badgePx: 10, badgePad: '4px 10px', helplinePx: 10,
    barcodeH: 28, barcodeBar: 1.0, barcodeFont: 0, barcodeBox: 132, barcodeLabelPx: 9,
    deliverPx: 9, namePx: 16, titlePx: 11, addrPx: 12, cityPx: 12, issuePx: 11,
    metaPx: 10, recipientGap: 6, fromPadTop: 6, wide: false, redBar: 4,
  },
  md: {
    logoH: 64, taglinePx: 11, padX: 22, padY: 14, headerPadTop: 20, bodyPadTop: 18, bodyPadBot: 10,
    logoMaxPct: '56%', sideMin: 168, badgePx: 12, badgePad: '6px 12px', helplinePx: 13, sideGap: 8,
    barcodeH: 34, barcodeBar: 1.15, barcodeFont: 0, barcodeBox: 158, barcodeLabelPx: 10,
    deliverPx: 11, namePx: 24, titlePx: 14, addrPx: 14, cityPx: 14, issuePx: 13,
    metaPx: 12, recipientGap: 11, fromPadTop: 10, wide: true, redBar: 5,
  },
  lg: {
    logoH: 72, taglinePx: 12, padX: 28, padY: 20, bodyPadTop: 22, bodyPadBot: 12,
    logoMaxPct: '50%', sideMin: 190, badgePx: 13, badgePad: '7px 14px', helplinePx: 14,
    barcodeH: 36, barcodeBar: 1.15, barcodeFont: 0, barcodeBox: 168, barcodeLabelPx: 11,
    deliverPx: 12, namePx: 28, titlePx: 15, addrPx: 16, cityPx: 16, issuePx: 15,
    metaPx: 13, recipientGap: 8, fromPadTop: 12, wide: true, redBar: 6,
  },
};

function BookMascot({ size = 78 }) {
  return (
    <svg
      className="slip-book-mascot"
      width={size}
      height={Math.round(size * 1.12)}
      viewBox="0 0 80 90"
      fill="none"
      aria-hidden="true"
    >
      <rect x="14" y="10" width="52" height="70" rx="5" fill="#fff" stroke="#111827" strokeWidth="2.4" />
      <rect x="18" y="14" width="44" height="12" rx="2" fill={FLARE_RED} />
      <text x="40" y="23" textAnchor="middle" fill="#fff" fontFamily="Arial, Helvetica, sans-serif" fontSize="7" fontWeight="800">FLARE</text>
      <circle cx="40" cy="42" r="11" fill="#fff7ed" stroke="#111827" strokeWidth="1.6" />
      <circle cx="36" cy="40" r="1.6" fill="#111827" />
      <circle cx="44" cy="40" r="1.6" fill="#111827" />
      <path d="M36 46c2.2 2.4 5.8 2.4 8 0" stroke="#111827" strokeWidth="1.6" strokeLinecap="round" />
      <rect x="22" y="56" width="36" height="4" rx="1.5" fill="#e5e7eb" />
      <rect x="22" y="63" width="28" height="3" rx="1.5" fill="#e5e7eb" />
      <path d="M10 18c0-4 4-8 10-8" stroke={FLARE_RED} strokeWidth="3" strokeLinecap="round" />
      <rect x="8" y="16" width="8" height="58" rx="2" fill={FLARE_RED} />
      <rect x="58" y="72" width="16" height="10" rx="2" fill="#111827" />
      <rect x="61" y="75" width="10" height="4" rx="0.8" fill="#fff" />
    </svg>
  );
}

function slipSizeFor(mode, perPage) {
  if (mode === 'thermal') return 'md';
  const n = Math.min(4, Math.max(1, Number(perPage) || 4));
  if (n <= 1) return 'lg';
  if (n <= 2) return 'md';
  return 'sm';
}

function SlipCard({ slip, branding, size = 'sm' }) {
  const t = SLIP_SCALE[size] || SLIP_SCALE.sm;
  const logo = branding?.slipLogoUrl || '/Flare Logo.png';
  const phone = branding?.slipCompanyPhone || '';
  const mobile = branding?.slipCompanyMobile || DEFAULT_MOBILE;
  const site = branding?.slipWebsiteUrl || 'digital.flare.pk';
  const lines = footerLines(branding?.deliveryFooter);
  const from = parseFromBlock(branding?.deliveryFromAddress);
  const fromOneLine = fromAddressOneLine(from.address);
  const fromAddrLines = fromAddressTwoLines(from.address);
  const badge = formatLabelType(slip.label_type);
  const isComplimentary = String(badge).toLowerCase() === 'complimentary';
  const titleLine = [slip.designation, slip.company].map((v) => String(v || '').trim()).filter(Boolean).join(' | ');
  const code = barcodeValue(slip);
  const trackingCode = String(slip?.tracking_number || '').trim();
  const footerText = t.wide ? lines.join('  ·  ') : null;

  const barcodeBox = (
    <div
      className="slip-barcode-box mx-auto flex items-center justify-center"
      style={{
        width: t.barcodeBox,
        maxWidth: '100%',
        height: t.barcodeH + 10,
        padding: '4px 6px',
        border: '1.5px solid #111827',
        borderRadius: 3,
        background: '#fff',
        boxSizing: 'border-box',
      }}
    >
      {code ? (
        <SlipBarcode value={code} height={t.barcodeH} barWidth={t.barcodeBar} fontSize={t.barcodeFont} />
      ) : null}
    </div>
  );

  const barcodeBlock = (
    <div
      className="slip-barcode-block flex flex-col items-center justify-center"
      style={{ textAlign: 'center', width: '100%', marginTop: 8 }}
    >
      <div
        className="font-extrabold uppercase text-gray-900 text-center whitespace-nowrap"
        style={{ fontSize: t.barcodeLabelPx, letterSpacing: '0.12em', marginBottom: 4 }}
      >
        Barcode
      </div>
      {barcodeBox}
    </div>
  );

  const scanPanel = (
    <div
      className="slip-scan-panel"
      style={{
        flex: '0 0 40%',
        maxWidth: '42%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        paddingLeft: 12,
        borderLeft: '1.5px solid #111827',
        boxSizing: 'border-box',
        minHeight: 0,
      }}
    >
      <BookMascot size={t.wide ? 96 : 70} />
      <div className="flex flex-col items-center text-center min-w-0">
        <div
          className="font-extrabold uppercase text-gray-900 whitespace-nowrap"
          style={{ fontSize: t.barcodeLabelPx + 1, letterSpacing: '0.18em', marginBottom: 6 }}
        >
          Scan me
        </div>
        <a
          href={siteUrl(site)}
          target="_blank"
          rel="noreferrer"
          className="slip-qr-wrap"
          style={{
            display: 'block',
            padding: 5,
            border: '1.5px solid #111827',
            borderRadius: 3,
            background: '#fff',
            lineHeight: 0,
          }}
        >
          <QRCodeSVG
            value={siteUrl(site)}
            size={t.wide ? 88 : 64}
            bgColor="#ffffff"
            fgColor="#111827"
            level="M"
            includeMargin={false}
          />
        </a>
        <div
          className="uppercase font-bold text-gray-900 whitespace-nowrap"
          style={{ fontSize: t.taglinePx, letterSpacing: '0.14em', marginTop: 6 }}
        >
          {site}
        </div>
        <div
          className="font-semibold text-gray-900"
          style={{ fontSize: t.barcodeLabelPx, marginTop: 3, lineHeight: 1.3 }}
        >
          Scan for website
        </div>
      </div>
    </div>
  );

  const { mobile: recipientMobile, ptcl: recipientPtcl } = slipPhones(slip);
  const cityPhoneLine = [slip.city, recipientMobile, recipientPtcl].filter(Boolean).join(' | ') || '—';
  const isWebsiteOrder = badge === 'Website Order';
  const orderNo = String(slip.order_number || slip.reference || '').trim();
  // Slip Amount = COD collect only (prepaid → Rs. 0)
  const isCod = Boolean(slip.is_cod) || String(slip.payment_method || '').toLowerCase() === 'cash';
  const collectPkr = isCod
    ? Number(slip.collect_amount_pkr ?? slip.amount_pkr ?? 0)
    : 0;
  const weightG = Number(slip.weight_grams || 500);
  const bookDate = String(slip.book_date || '').trim();
  const amountText = `Rs. ${collectPkr.toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
  const weightText = `${weightG.toFixed(2)} (g)`;
  const metaLabelPx = Math.max(7, t.metaPx - 3);
  const metaValuePx = t.metaPx + (t.wide ? 1 : 0);

  const websiteOrderMeta = isWebsiteOrder ? (
    <div
      className="slip-order-meta"
      style={{
        flex: '0 0 auto',
        padding: t.wide ? `7px ${t.padX}px 8px` : `5px ${t.padX}px 6px`,
        borderBottom: '1.5px solid #111827',
        background: '#fafafa',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.35fr 0.9fr 0.95fr 1fr',
          gap: t.wide ? 10 : 6,
          alignItems: 'end',
        }}
      >
        <div className="min-w-0">
          <div className="uppercase font-bold text-gray-500" style={{ fontSize: metaLabelPx, letterSpacing: '0.12em', lineHeight: 1.2 }}>
            Order ID
          </div>
          <div className="font-extrabold text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: metaValuePx, letterSpacing: '0.02em', marginTop: 2 }}>
            {orderNo || '—'}
          </div>
        </div>
        <div className="min-w-0">
          <div className="uppercase font-bold text-gray-500" style={{ fontSize: metaLabelPx, letterSpacing: '0.12em', lineHeight: 1.2 }}>
            Weight
          </div>
          <div className="font-bold text-gray-900 whitespace-nowrap" style={{ fontSize: metaValuePx, marginTop: 2 }}>
            {weightText}
          </div>
        </div>
        <div className="min-w-0">
          <div className="uppercase font-bold text-gray-500" style={{ fontSize: metaLabelPx, letterSpacing: '0.12em', lineHeight: 1.2 }}>
            Book Date
          </div>
          <div className="font-bold text-gray-900 whitespace-nowrap" style={{ fontSize: metaValuePx, marginTop: 2 }}>
            {bookDate || '—'}
          </div>
        </div>
        <div className="min-w-0 text-right">
          <div className="uppercase font-bold text-gray-500" style={{ fontSize: metaLabelPx, letterSpacing: '0.12em', lineHeight: 1.2 }}>
            Amount
          </div>
          <div
            className="font-black text-gray-900 leading-none whitespace-nowrap"
            style={{
              fontSize: isCod ? (t.wide ? 18 : 14) : metaValuePx,
              letterSpacing: '0.01em',
              marginTop: 2,
            }}
          >
            {amountText}
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const recipientFields = (
    <>
      <div className="uppercase tracking-[0.16em] text-gray-900 font-bold" style={{ fontSize: t.deliverPx }}>Deliver to</div>
      <div
        className={`font-extrabold text-gray-900 leading-tight ${t.wide ? '' : 'whitespace-nowrap overflow-hidden text-ellipsis'}`}
        style={{ fontSize: t.namePx }}
      >
        {slip.name || '—'}
      </div>
      {isComplimentary && titleLine ? (
        <div className={`text-gray-900 font-semibold ${t.wide ? '' : 'whitespace-nowrap overflow-hidden text-ellipsis'}`} style={{ fontSize: t.titlePx }}>
          {titleLine}
        </div>
      ) : null}
      <div className={`text-gray-900 leading-snug ${t.wide ? '' : 'line-clamp-2'}`} style={{ fontSize: t.addrPx, lineHeight: 1.35 }}>{slip.address || '—'}</div>
      <div className="text-gray-900 font-bold whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: t.cityPx }}>
        {cityPhoneLine}
      </div>
      <div className="text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: t.issuePx }}>
        <span className="font-extrabold">Issue</span>
        <span className="text-gray-900"> | </span>
        <span className="font-semibold">{issueLine(slip)}</span>
      </div>
    </>
  );

  return (
    <div
      className="slip-card bg-white flex flex-col h-full"
      style={{
        fontFamily: 'Arial, Helvetica, sans-serif',
        padding: 0,
        boxSizing: 'border-box',
        borderTop: `${t.redBar}px solid ${FLARE_RED}`,
        borderRight: '2px solid #111827',
        borderBottom: '2px solid #111827',
        borderLeft: '2px solid #111827',
        borderRadius: 8,
        textAlign: 'left',
        color: '#111827',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
    >
      <div className="slip-card-header" style={{ padding: `${t.headerPadTop || t.padY}px ${t.padX}px ${t.padY}px ${t.padX}px`, borderBottom: '2px solid #111827' }}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col items-stretch min-w-0 overflow-hidden" style={{ flex: '1 1 auto', maxWidth: trackingCode && !isComplimentary ? '34%' : t.logoMaxPct }}>
            <div
              className="slip-logo-wrap"
              style={{
                height: `${t.logoH}px`,
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                margin: 0,
                padding: 0,
                display: 'block',
                lineHeight: 0,
              }}
            >
              <img
                src={logo}
                alt="Flare"
                style={{
                  height: `${t.logoH}px`,
                  width: 'auto',
                  maxWidth: 'none',
                  maxHeight: `${t.logoH}px`,
                  objectFit: 'contain',
                  objectPosition: 'left center',
                  display: 'block',
                  margin: 0,
                  padding: 0,
                }}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/Flare Logo.png'; }}
              />
            </div>
            <div
              className="uppercase font-bold leading-none text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis"
              style={{
                fontSize: `${t.taglinePx}px`,
                maxWidth: '100%',
                marginTop: 4,
                marginLeft: t.wide ? 12 : 6,
                letterSpacing: '0.40em',
              }}
            >
              {site}
            </div>
          </div>
          {trackingCode && !isComplimentary ? (
            <div
              className="slip-header-tracking flex flex-col items-center justify-center min-w-0"
              style={{ flex: '1 1 36%', padding: '0 6px' }}
            >
              <div
                className="font-extrabold uppercase text-gray-900 text-center"
                style={{ fontSize: Math.max(8, t.barcodeLabelPx - 1), letterSpacing: '0.14em', marginBottom: 2 }}
              >
                {String(slip.courier || 'Leopards')} tracking
              </div>
              <div
                className="font-black text-gray-900 text-center leading-none"
                style={{ fontSize: t.wide ? 13 : 11, letterSpacing: '0.06em', marginBottom: 4 }}
              >
                {trackingCode}
              </div>
              <div
                className="slip-barcode-box flex items-center justify-center"
                style={{
                  width: Math.min(t.barcodeBox, 150),
                  maxWidth: '100%',
                  height: t.barcodeH + 6,
                  padding: '3px 5px',
                  border: '1.5px solid #111827',
                  borderRadius: 3,
                  background: '#fff',
                  boxSizing: 'border-box',
                }}
              >
                <SlipBarcode value={trackingCode} height={t.barcodeH} barWidth={t.barcodeBar} fontSize={0} />
              </div>
            </div>
          ) : null}
          <div
            className="shrink-0 flex flex-col items-end text-right"
            style={{ minWidth: t.sideMin, maxWidth: t.wide ? '34%' : undefined, gap: t.sideGap || 6 }}
          >
            <span
              className="font-extrabold text-white rounded uppercase tracking-wide"
              style={{ backgroundColor: FLARE_RED, fontSize: t.badgePx, padding: t.badgePad }}
            >
              {badge}
            </span>
            {phone ? (
              <div className="font-bold text-gray-900 leading-tight" style={{ fontSize: t.helplinePx }}>
                Helpline {phone}
              </div>
            ) : null}
            {mobile ? (
              <div className="font-bold text-gray-900 leading-tight" style={{ fontSize: t.helplinePx }}>
                Mobile {mobile}
              </div>
            ) : null}
          </div>
        </div>
        {!t.wide && !(trackingCode && !isComplimentary) ? barcodeBlock : null}
      </div>

      {websiteOrderMeta}

      <div
        className="slip-card-body flex-1 min-w-0 flex"
        style={{
          padding: `${t.bodyPadTop}px ${t.padX}px ${t.bodyPadBot}px`,
          textAlign: 'left',
          flexDirection: t.wide ? 'row' : 'column',
          alignItems: t.wide ? 'stretch' : 'stretch',
          gap: t.wide ? 8 : 0,
        }}
      >
        <div
          className="slip-card-recipient"
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: t.wide ? '1 1 58%' : '1 1 auto',
            justifyContent: t.wide ? 'center' : 'flex-start',
            minWidth: 0,
            minHeight: 0,
            gap: t.recipientGap,
          }}
        >
          {recipientFields}
        </div>
        {t.wide ? scanPanel : null}
      </div>
      <div className="slip-card-from" style={{ flex: '0 0 auto', padding: `0 ${t.padX}px ${t.bodyPadBot}px`, paddingTop: t.fromPadTop }}>
          {t.wide ? (
            <div className="text-gray-900" style={{ fontSize: t.metaPx, lineHeight: 1.35 }}>
              <span className="font-extrabold">From: </span>
              <span className="font-semibold">{fromOneLine}</span>
              {from.phone ? <span className="font-bold"> · {from.phone}</span> : null}
            </div>
          ) : (
            <>
              {fromAddrLines.map((line, i) => (
                <div
                  key={`from-${i}`}
                  className="text-gray-900"
                  style={{ fontSize: t.metaPx, lineHeight: 1.4 }}
                >
                  {i === 0 ? <span className="font-extrabold">From: </span> : null}
                  <span className="font-semibold">{line}</span>
                </div>
              ))}
              {from.phone ? (
                <div className="text-gray-900 font-bold" style={{ fontSize: t.metaPx, lineHeight: 1.4 }}>
                  {from.phone}
                </div>
              ) : null}
            </>
          )}
      </div>

      <div
        className="slip-card-footer"
        style={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: t.wide ? `12px ${t.padX}px` : `8px ${t.padX}px`,
          minHeight: t.wide ? 44 : 32,
          borderTop: '2px solid #111827',
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >
        {t.wide ? (
          <div className="font-bold text-gray-900" style={{ fontSize: t.metaPx, lineHeight: 1.4, textAlign: 'center' }}>
            {footerText}
          </div>
        ) : (
          lines.map((line, i) => (
            <div
              key={`${line}-${i}`}
              className={i === 0 ? 'font-bold text-gray-900' : 'text-gray-900'}
              style={{ fontSize: t.metaPx, lineHeight: 1.45, textAlign: 'center' }}
            >
              {line}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function SlipPreview({ slips, branding, onClose, onPrint }) {
  if (!slips?.length) return null;
  const mode = branding?.slipPrinterMode === 'thermal' ? 'thermal' : 'a4';
  const perPage = Math.min(4, Math.max(1, Number(branding?.slipA4PerPage) || 4));
  const size = slipSizeFor(mode, perPage);
  const a4Cols = perPage >= 4 ? 2 : 1;
  const a4Rows = perPage >= 4 ? 2 : perPage;
  const a4Gap = mode === 'thermal' ? '0' : (perPage <= 2 ? '8mm' : '2.5mm');
  const a4PageH = mode === 'thermal' ? '142mm' : '287mm';
  const previewMinH = size === 'lg' ? 520 : size === 'md' ? 460 : 340;
  const pages = [];
  const step = mode === 'thermal' ? 1 : perPage;
  for (let i = 0; i < slips.length; i += step) {
    const chunk = slips.slice(i, i + step);
    if (mode === 'a4') {
      while (chunk.length < perPage) chunk.push(null);
    }
    pages.push(chunk);
  }
  const previewPages = pages.slice(0, 2);
  const previewSlipCount = previewPages.reduce((n, chunk) => n + chunk.filter(Boolean).length, 0);

  const printPages = (
    <div data-slip-print-root className="slip-print-root">
      {pages.map((chunk, pageIdx) => (
        <div key={`print-page-${pageIdx}`} className="slip-print-page">
          {chunk.map((s, idx) => (
            s ? (
              <SlipCard key={s.id || s.reference || idx} slip={s} branding={branding} size={size} />
            ) : (
              <div key={`empty-${pageIdx}-${idx}`} className="slip-print-empty" />
            )
          ))}
        </div>
      ))}
    </div>
  );

  const previewGrid = mode === 'thermal'
    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'
    : perPage >= 4
      ? 'grid grid-cols-1 sm:grid-cols-2 gap-3'
      : 'grid grid-cols-1 gap-4 max-w-3xl mx-auto';

  return (
    <>
    <style>{`
        .slip-print-root { display: none; }
        @media print {
          @page { size: ${mode === 'thermal' ? '100mm 150mm' : 'A4 portrait'}; margin: ${mode === 'thermal' ? '4mm' : '5mm'}; }
          html, body {
            background: #fff !important;
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            overflow: visible !important;
          }
          body > *:not([data-slip-print-root]) { display: none !important; }
          body > [data-slip-print-root] {
            display: block !important;
            position: static !important;
            width: ${mode === 'thermal' ? '92mm' : '200mm'} !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
            overflow: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .slip-print-page {
            box-sizing: border-box;
            display: grid !important;
            width: ${mode === 'thermal' ? '92mm' : '200mm'};
            height: ${a4PageH};
            padding: 0;
            margin: 0;
            grid-template-columns: ${mode === 'thermal' ? '1fr' : `repeat(${a4Cols}, 1fr)`};
            grid-template-rows: ${mode === 'thermal' ? '1fr' : `repeat(${a4Rows}, 1fr)`};
            gap: ${a4Gap};
            align-content: stretch;
            page-break-after: always;
            break-after: page;
            page-break-inside: avoid;
            break-inside: avoid;
            overflow: visible;
          }
          .slip-print-page:last-child { page-break-after: auto; break-after: auto; }
          .slip-print-page > * { min-height: 0; overflow: visible; height: 100%; display: flex !important; box-sizing: border-box !important; }
          .slip-card {
            display: flex !important;
            flex-direction: column !important;
            height: 100% !important;
            box-sizing: border-box !important;
            border-top: ${size === 'lg' ? 6 : size === 'md' ? 5 : 4}px solid #c4151c !important;
            border-right: 2px solid #111827 !important;
            border-bottom: 2px solid #111827 !important;
            border-left: 2px solid #111827 !important;
            border-radius: 6px !important;
            background: #fff !important;
            color: #111827 !important;
            overflow: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .slip-logo-wrap { overflow: hidden !important; display: block !important; width: 100% !important; line-height: 0 !important; }
          .slip-card-header {
            display: block !important;
            flex: 0 0 auto !important;
            border-bottom: 2px solid #111827 !important;
          }
          .slip-barcode-block {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            text-align: center !important;
          }
          .slip-barcode-box {
            border: 1.5px solid #111827 !important;
            background: #fff !important;
            box-sizing: border-box !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .barcode-wrap { display: flex !important; align-items: center !important; justify-content: center !important; width: 100% !important; height: 100% !important; }
          .barcode-wrap svg { display: block !important; margin: 0 auto !important; max-width: 100% !important; }
          .slip-qr-wrap { display: block !important; background: #fff !important; border: 1.5px solid #111827 !important; }
          .slip-qr-wrap svg { display: block !important; }
          .slip-card-body {
            display: flex !important;
            flex-direction: ${size === 'sm' ? 'column' : 'row'} !important;
            align-items: stretch !important;
            flex: 1 1 auto !important;
            min-height: 0 !important;
            overflow: hidden !important;
          }
          .slip-scan-panel {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            flex: 0 0 40% !important;
            border-left: 1.5px solid #111827 !important;
            box-sizing: border-box !important;
          }
          .slip-book-mascot { display: block !important; flex-shrink: 0 !important; }
          .slip-card-recipient {
            display: flex !important;
            flex-direction: column !important;
            flex: 1 1 auto !important;
            justify-content: ${size === 'sm' ? 'flex-start' : 'center'} !important;
            min-height: 0 !important;
            overflow: hidden !important;
          }
          .slip-card-from {
            flex: 0 0 auto !important;
            margin-top: 0 !important;
            overflow: visible !important;
          }
          .slip-card-footer {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            flex: 0 0 auto !important;
            text-align: center !important;
            border-top: 2px solid #111827 !important;
            overflow: visible !important;
            min-height: ${size === 'sm' ? '32px' : '44px'} !important;
            box-sizing: border-box !important;
          }
          .slip-print-empty { border: none; background: transparent; display: block !important; }
          [data-slip-print-root] img { display: block !important; }
        }
      `}</style>
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4 sm:p-6 print:hidden" data-testid="slip-preview">
      <div className={`bg-white rounded-xl w-full p-5 print:hidden border border-gray-100 shadow-xl ${perPage <= 2 && mode === 'a4' ? 'max-w-3xl' : 'max-w-5xl'}`}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-extrabold text-lg text-gray-900 tracking-tight">Packing slip preview</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Printer: <span className="font-semibold uppercase">{mode}</span>
              {mode === 'a4' ? ` · ${perPage} ${perPage === 1 ? 'slip' : 'slips'} / A4 page · ${size === 'sm' ? 'compact type' : 'professional type'}` : ' · 1 slip / 4×6 label'}
              {slips.length > previewSlipCount ? ` · Preview ${previewSlipCount} of ${slips.length} · Print includes all` : ` · ${slips.length} ${slips.length === 1 ? 'slip' : 'slips'}`}
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onPrint} className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white rounded-lg font-bold shadow" style={{ backgroundColor: FLARE_RED }}>Print</button>
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg bg-white font-semibold text-gray-800 hover:bg-gray-50">Close</button>
          </div>
        </div>
        {mode === 'a4' && perPage <= 2 ? (
          <div className="space-y-6">
            {previewPages.map((chunk, pageIdx) => (
              <div
                key={`preview-page-${pageIdx}`}
                className="mx-auto bg-gray-100 rounded-xl p-4 border border-gray-100"
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400 text-center mb-3">
                  A4 page {pageIdx + 1} · {perPage} stacked {perPage === 1 ? 'slip' : 'slips'}
                </div>
                <div
                  className="mx-auto bg-white shadow-md border border-gray-200 p-2.5"
                  style={{ maxWidth: 640 }}
                >
                  <div
                    className="grid"
                    style={{
                      gridTemplateRows: `repeat(${perPage}, minmax(${previewMinH}px, auto))`,
                      gap: perPage <= 2 ? 18 : 10,
                    }}
                  >
                    {chunk.map((s, idx) => (
                      s ? (
                        <SlipCard key={s.id || s.reference || idx} slip={s} branding={branding} size={size} />
                      ) : (
                        <div key={`preview-empty-${pageIdx}-${idx}`} className="rounded-lg border border-dashed border-gray-200 bg-gray-50" />
                      )
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`${previewGrid} bg-gray-100 rounded-xl p-4 border border-gray-100`}>
            {slips.slice(0, previewSlipCount).map((s) => (
              <div key={s.id || s.reference} className="w-full" style={{ minHeight: previewMinH }}>
                <SlipCard slip={s} branding={branding} size={size} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    {typeof document !== 'undefined' ? createPortal(printPages, document.body) : printPages}
    </>
  );
}

export function SlipPreviewDemo() {
  const perPage = Math.min(4, Math.max(1, Number(new URLSearchParams(window.location.search).get('per')) || 2));
  const mock = [
    {
      id: 1,
      name: 'Ahmed Khan',
      address: 'House 12, Street 4, Block C, Gulberg III',
      city: 'Lahore',
      phone: '0300-1234567',
      landline: '042-35771234',
      issue: 'Flare Magazine',
      issue_month: 'September 2026',
      barcode: 'FLR10021',
      label_type: 'Paid Sub',
    },
    {
      id: 2,
      name: 'Sara Malik',
      designation: 'Director',
      company: 'Media House',
      address: 'Office 8, 2nd Floor, MM Alam Road',
      city: 'Karachi',
      phone: '0321-5558899',
      landline: '021-35661234',
      issue: 'Flare Magazine',
      issue_month: 'September 2026',
      barcode: 'FLR10022',
      label_type: 'Complimentary',
    },
    {
      id: 3,
      name: 'Usman Raza',
      address: 'Plot 22, Industrial Area, I-9',
      city: 'Islamabad',
      phone: '0333-4447788',
      landline: '051-1112233',
      issue: 'Flare Magazine',
      issue_month: 'September 2026',
      barcode: 'FLR10023',
      label_type: 'Paid Sub',
    },
    {
      id: 4,
      name: 'Hina Tariq',
      address: 'Street 9, Satellite Town',
      city: 'Rawalpindi',
      phone: '0345-2211000',
      issue: 'Flare Magazine',
      issue_month: 'September 2026',
      barcode: 'FLR10024',
      label_type: 'Paid Sub',
    },
  ];
  const branding = {
    slipPrinterMode: 'a4',
    slipA4PerPage: perPage,
    slipLogoUrl: '/Flare Logo.png',
    slipWebsiteUrl: 'digital.flare.pk',
    slipCompanyPhone: '042-111-123-456',
    slipCompanyMobile: DEFAULT_MOBILE,
    deliveryFromAddress: `${DEFAULT_FROM_ADDRESS}\n${DEFAULT_FROM_PHONE}`,
    deliveryFooter: DEFAULT_FOOTER,
  };
  return (
    <SlipPreview
      slips={mock.slice(0, Math.max(perPage, 2))}
      branding={branding}
      onClose={() => {}}
      onPrint={() => window.print()}
    />
  );
}

function PackingStepper({ phase, onGo, can2, can3 }) {
  const steps = [
    { n: 1, label: 'Select issue', hint: 'Magazine month first' },
    { n: 2, label: 'Print slips', hint: 'This issue only', locked: !can2 },
    { n: 3, label: 'Delivery', hint: 'Manual TCS, then finish', locked: !can3 },
  ];
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 sm:p-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {steps.map((s) => {
          const active = Number(phase) === s.n;
          const done = Number(phase) > s.n;
          return (
            <button
              key={s.n}
              type="button"
              disabled={s.locked && !done && !active}
              onClick={() => {
                if (s.n === 1) onGo(1);
                if (s.n === 2 && can2) onGo(2);
                if (s.n === 3 && can3) onGo(3);
              }}
              className={`text-left rounded-xl border px-4 py-3 ${
                active ? 'border-rose-300 bg-rose-50/70 ring-2 ring-rose-100' : done ? 'border-emerald-200 bg-emerald-50/40' : s.locked ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold text-white shrink-0"
                  style={{ backgroundColor: done ? '#059669' : active ? FLARE_RED : s.locked ? '#9ca3af' : '#111827' }}
                >
                  {done ? '✓' : s.n}
                </span>
                <div>
                  <div className="text-sm font-extrabold text-gray-900">Step {s.n}: {s.label}</div>
                  <div className="text-xs text-gray-500">{s.locked && !active && !done ? 'Finish the previous step first' : s.hint}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function PackingDeskSection() {
  const app = useApp() || {};
  const [searchParams] = useSearchParams();
  const [issues, setIssues] = useState([]);
  const [items, setItems] = useState([]);
  const [issueId, setIssueId] = useState(searchParams.get('issue') || '');
  const [loadedIssueId, setLoadedIssueId] = useState('');
  const [printedSession, setPrintedSession] = useState(false);
  const [phase, setPhase] = useState('home');
  const [lastRun, setLastRun] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('flare_packing_last_run') || 'null');
    } catch {
      return null;
    }
  });
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [manualTracking, setManualTracking] = useState('');
  const [preview, setPreview] = useState(null);
  const loadGen = useRef(0);

  const load = useCallback(async () => {
    const gen = ++loadGen.current;
    setLoading(true);
    setLoadError('');
    try {
      const params = {};
      if (issueId) params.magazine_issue_id = issueId;
      const { data } = await dispatchApi.queue(params);
      if (gen !== loadGen.current) return;
      setIssues(data.issues || []);
      if (!issueId) {
        setItems([]);
        setLoadedIssueId('');
        return;
      }
      const packing = (data.items || []).filter((row) => !isWebsitePackItem(row));
      setItems(packing);
      setLoadedIssueId(String(issueId));
    } catch (e) {
      if (gen !== loadGen.current) return;
      const msg = e?.response?.data?.message || 'Failed to load packing queue';
      setLoadError(msg);
      toast.error(msg);
    } finally {
      if (gen === loadGen.current) setLoading(false);
    }
  }, [issueId]);

  useEffect(() => {
    load();
  }, [load]);

  const allIds = useMemo(() => items.map((i) => i.id), [items]);
  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const retryCount = useMemo(() => items.filter((i) => i.status === 'NeedsRetry').length, [items]);
  const printedCount = useMemo(
    () => items.filter((i) => ['SlipPrinted', 'Shipped', 'EmailSent'].includes(i.status)).length,
    [items],
  );
  const shippedCount = useMemo(
    () => items.filter((i) => ['Shipped', 'EmailSent'].includes(i.status)).length,
    [items],
  );
  const complimentaryCount = useMemo(
    () => items.filter((i) => formatLabelType(i.label_type) === 'Complimentary').length,
    [items],
  );
  const paidCount = useMemo(
    () => items.filter((i) => formatLabelType(i.label_type) === 'Paid Sub').length,
    [items],
  );
  const queuedCount = useMemo(() => items.filter((i) => i.status === 'Queued').length, [items]);
  const noEmailCount = useMemo(() => items.filter((i) => !displayEmail(i.recipient_email)).length, [items]);
  const selectedIssue = useMemo(() => issues.find((iss) => String(iss.id) === String(issueId)), [issues, issueId]);
  const issueName = selectedIssue
    ? `${selectedIssue.title}${selectedIssue.month_year ? ` · ${selectedIssue.month_year}` : ''}`
    : '';
  const step1Ready = Boolean(issueId) && String(loadedIssueId) === String(issueId) && !loadError && items.length > 0;
  const step2Ready = step1Ready && (printedSession || printedCount > 0);

  useEffect(() => {
    setPrintedSession(false);
    setSelected(new Set());
  }, [issueId]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(allIds));
  };

  const requireSelection = () => {
    if (!selectedIds.length) {
      toast.warn('Select at least one row');
      return false;
    }
    return true;
  };

  const printOne = async (id) => {
    setBusy(true);
    try {
      const { data } = await dispatchApi.printSlips([id]);
      setPreview(data.slips || []);
      setPrintedSession(true);
      toast.success('Slip preview ready');
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || 'Print failed');
    } finally {
      setBusy(false);
    }
  };

  const runAction = async (fn, successMsg, { reload = true, keepSelection = false } = {}) => {
    if (!requireSelection()) return;
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      if (!keepSelection) setSelected(new Set());
      if (reload) await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.response?.data?.error || e.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const goPrint = () => {
    if (!issueId || String(loadedIssueId) !== String(issueId)) {
      toast.warn('Select the magazine issue first');
      return;
    }
    if (!items.length) {
      toast.warn('Generate or reload the queue for this issue before printing');
      return;
    }
    setSelected(new Set(allIds));
    persistProgress(false);
    setPhase(2);
  };

  const goDelivery = () => {
    if (!step2Ready) {
      toast.warn('Print slips for this issue first so the wrong magazine cannot be shipped');
      return;
    }
    setPhase(3);
  };

  const persistProgress = (finished = false) => {
    if (!issueId && !selectedIssue) return null;
    const snap = {
      issueId,
      title: selectedIssue?.title || 'Flare Magazine',
      month: selectedIssue?.month_year || '',
      users: items.length,
      printed: printedCount,
      shipped: shippedCount,
      complimentary: complimentaryCount,
      paid: paidCount,
      queued: queuedCount,
      retry: retryCount,
      noEmail: noEmailCount,
      finished: Boolean(finished),
      at: new Date().toISOString(),
    };
    setLastRun(snap);
    try {
      sessionStorage.setItem('flare_packing_last_run', JSON.stringify(snap));
    } catch {
      /* ignore */
    }
    return snap;
  };

  const goHome = () => {
    persistProgress(false);
    setPhase('home');
  };

  const finishRun = () => {
    persistProgress(true);
    setPhase('home');
    toast.success(`Finished ${issueName || 'this issue'} · ${items.length} recipients`);
  };

  const startFromIssue = (id) => {
    if (id) setIssueId(String(id));
    setPhase(1);
  };

  const resumePacking = () => {
    if (lastRun?.issueId) setIssueId(String(lastRun.issueId));
    if ((lastRun?.printed || printedCount) > 0 && (lastRun?.users || items.length) > 0) setPhase(2);
    else setPhase(1);
  };

  const homeUsers = lastRun?.users ?? items.length;
  const homePrinted = lastRun?.printed ?? printedCount;
  const homeShipped = lastRun?.shipped ?? shippedCount;
  const homeRetry = lastRun?.retry ?? retryCount;
  const homeComp = items.length ? complimentaryCount : (typeof lastRun?.complimentary === 'number' ? lastRun.complimentary : '—');
  const homePaid = items.length ? paidCount : (typeof lastRun?.paid === 'number' ? lastRun.paid : '—');
  const homeQueued = items.length ? queuedCount : (typeof lastRun?.queued === 'number' ? lastRun.queued : '—');
  const homeNoEmail = items.length ? noEmailCount : (typeof lastRun?.noEmail === 'number' ? lastRun.noEmail : '—');
  const homeTitle = lastRun
    ? `${lastRun.title || 'Flare Magazine'}${lastRun.month ? ` · ${lastRun.month}` : ''}`
    : issueName;
  const hasHomeIssue = Boolean(lastRun || (issueName && items.length));
  const printerLabel = app.slipPrinterMode === 'thermal'
    ? 'Thermal 4×6'
    : `A4 · ${Math.min(4, Math.max(1, Number(app.slipA4PerPage) || 4))} slips / page`;
  const remainPrint = Math.max(0, homeUsers - homePrinted);
  const remainShip = Math.max(0, homeUsers - homeShipped);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="admin-page-title">Packing Desk</h1>
          <p className="admin-page-sub">
            One magazine issue at a time. Single-issue website customers pack from Website Orders.
          </p>
        </div>
        {phase !== 'home' ? (
          <button
            type="button"
            onClick={goHome}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-800 rounded-lg shadow-sm hover:bg-gray-50 font-semibold text-sm"
          >
            Back to summary
          </button>
        ) : null}
      </div>

      {phase === 'home' ? (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <div className="text-sm font-extrabold text-gray-900">How packing works</div>
                <p className="text-xs text-gray-500 mt-0.5">Three locked steps. Finish one, then the next opens.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { n: 1, title: 'Select issue', body: 'Pick the magazine month, then generate complimentary copies plus paid plans entitled to this issue.' },
                { n: 2, title: 'Print slips', body: 'Print only this issue. Preview opens before paper so the wrong month cannot go out.' },
                { n: 3, title: 'Delivery', body: 'Mark shipped with optional TCS tracking, send emails if they have one, then finish.' },
              ].map((row) => (
                <div key={row.n} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold text-white" style={{ backgroundColor: FLARE_RED }}>{row.n}</span>
                    <div className="text-sm font-extrabold text-gray-900">{row.title}</div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500 leading-relaxed">{row.body}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="absolute inset-y-0 left-0 w-1.5" style={{ backgroundColor: FLARE_RED }} />
            <div className="p-6 sm:p-8 pl-7 sm:pl-9">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400">Packing operations</span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${
                        lastRun?.finished
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-100'
                          : hasHomeIssue
                            ? 'bg-amber-50 text-amber-800 border-amber-100'
                            : 'bg-gray-50 text-gray-600 border-gray-200'
                      }`}
                    >
                      {lastRun?.finished ? 'Last run finished' : hasHomeIssue ? 'In progress' : 'Ready'}
                    </span>
                  </div>
                  <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-gray-900">
                    {hasHomeIssue ? (homeTitle || 'Flare Magazine') : 'No issue packed yet'}
                  </h2>
                  <p className="mt-2 text-sm text-gray-500 max-w-2xl">
                    {hasHomeIssue
                      ? `Month ${lastRun?.month || selectedIssue?.month_year || '—'} · ${homeUsers} recipient${homeUsers === 1 ? '' : 's'} · Printer ${printerLabel}`
                      : 'Select one magazine month, generate the queue, print slips, then mark delivery. Single-issue website customers stay on Website Orders.'}
                  </p>
                  {lastRun?.at ? (
                    <p className="mt-1 text-xs text-gray-400">Updated {formatDeskTime(lastRun.at)}</p>
                  ) : null}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                  {hasHomeIssue && !lastRun?.finished ? (
                    <button
                      type="button"
                      onClick={resumePacking}
                      className="px-5 py-2.5 rounded-lg text-white text-sm font-bold shadow"
                      style={{ backgroundColor: FLARE_RED }}
                    >
                      Resume packing
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startFromIssue(lastRun?.issueId)}
                      className="px-5 py-2.5 rounded-lg text-white text-sm font-bold shadow"
                      style={{ backgroundColor: FLARE_RED }}
                    >
                      Start packing — Step 1
                    </button>
                  )}
                  {lastRun?.finished ? (
                    <button
                      type="button"
                      onClick={() => startFromIssue(lastRun.issueId)}
                      className="px-5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50"
                    >
                      Pack this issue again
                    </button>
                  ) : null}
                </div>
              </div>

              {hasHomeIssue ? (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-1.5">
                      <span>Slips printed</span>
                      <span className="text-gray-900">{homePrinted}/{homeUsers} · {pct(homePrinted, homeUsers)}%</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct(homePrinted, homeUsers)}%`, backgroundColor: FLARE_RED }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-1.5">
                      <span>Marked shipped</span>
                      <span className="text-gray-900">{homeShipped}/{homeUsers} · {pct(homeShipped, homeUsers)}%</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-600" style={{ width: `${pct(homeShipped, homeUsers)}%` }} />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Recipients" value={hasHomeIssue ? homeUsers : '—'} hint="This issue only" icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" iconBg={FLARE_RED} />
            <StatCard label="Printed" value={hasHomeIssue ? homePrinted : '—'} hint={remainPrint ? `${remainPrint} still to print` : 'All printed'} icon="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" iconBg="#4f46e5" />
            <StatCard label="Shipped" value={hasHomeIssue ? homeShipped : '—'} hint={remainShip ? `${remainShip} still to ship` : 'All shipped'} icon="M8 17a2 2 0 11-4 0 2 2 0 014 0zM20 17a2 2 0 11-4 0 2 2 0 014 0zM9 5h6l3 4v6H6V9l3-4z" iconBg="#059669" />
            <StatCard label="Retry" value={hasHomeIssue ? homeRetry : '—'} hint="Needs staff follow-up" icon="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" iconBg={homeRetry ? '#b45309' : '#6b7280'} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="text-sm font-extrabold text-gray-900">Recipient mix</div>
              <p className="text-xs text-gray-500 mt-0.5 mb-4">Complimentary copies and paid physical subscribers for this issue.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Complimentary', value: hasHomeIssue ? homeComp : '—' },
                  { label: 'Paid physical', value: hasHomeIssue ? homePaid : '—' },
                  { label: 'Still queued', value: hasHomeIssue ? homeQueued : '—' },
                  { label: 'No email', value: hasHomeIssue ? homeNoEmail : '—' },
                ].map((row) => (
                  <div key={row.label} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                    <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400">{row.label}</div>
                    <div className="mt-1 text-2xl font-black text-gray-900">{row.value}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-xl border border-rose-100 bg-rose-50/70 px-4 py-3 text-sm text-rose-900">
                <span className="font-bold">One magazine at a time.</span> Wrong issue selected means the wrong magazine goes in the post. Website orders are packed separately.
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="text-sm font-extrabold text-gray-900">Run checklist</div>
              <p className="text-xs text-gray-500 mt-0.5 mb-4">Staff path so nobody skips a step.</p>
              <ol className="space-y-3">
                {[
                  { n: 1, label: 'Select issue & generate queue', done: homeUsers > 0 },
                  { n: 2, label: 'Print packing slips', done: homePrinted > 0 },
                  { n: 3, label: 'Mark shipped (manual TCS)', done: homeShipped > 0 },
                  { n: 4, label: 'Finish this issue', done: Boolean(lastRun?.finished) },
                ].map((row) => (
                  <li key={row.n} className="flex items-start gap-3">
                    <span
                      className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-extrabold text-white shrink-0 mt-0.5"
                      style={{ backgroundColor: row.done ? '#059669' : '#9ca3af' }}
                    >
                      {row.done ? '✓' : row.n}
                    </span>
                    <span className={`text-sm ${row.done ? 'text-gray-500 line-through' : 'font-semibold text-gray-800'}`}>{row.label}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <div className="text-sm font-extrabold text-gray-900">Magazine issues</div>
                <p className="text-xs text-gray-500 mt-0.5">Start packing from the month you are sending. Do not mix issues.</p>
              </div>
              <Link to="/magazine/issues" className="text-xs font-bold hover:underline" style={{ color: FLARE_RED }}>Manage issues</Link>
            </div>
            {issues.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                {issues.slice(0, 8).map((iss) => {
                  const active = String(lastRun?.issueId || issueId) === String(iss.id);
                  return (
                    <button
                      key={iss.id}
                      type="button"
                      onClick={() => startFromIssue(iss.id)}
                      className={`text-left rounded-xl border p-4 hover:border-rose-200 hover:bg-rose-50/40 transition ${
                        active ? 'border-rose-300 bg-rose-50/70 ring-1 ring-rose-100' : 'border-gray-100 bg-white'
                      }`}
                    >
                      <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400">{iss.month_year || 'Issue'}</div>
                      <div className="mt-1 text-sm font-extrabold text-gray-900 leading-snug">{iss.title}</div>
                      <div className="mt-3 text-xs font-semibold" style={{ color: FLARE_RED }}>Pack this issue →</div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500">{loading ? 'Loading issues…' : 'No magazine issues found.'}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { to: '/magazine/orders', title: 'Website orders', hint: 'Singles + plan verify' },
              { to: '/magazine/subscribers', title: 'Subscribers', hint: 'Complimentary + paid list' },
              { to: '/magazine/delivery', title: 'Delivery reports', hint: 'After slips go out' },
              { to: '/admin/plugins', title: 'Plugins', hint: 'Email / TCS settings' },
            ].map((row) => (
              <Link
                key={row.to}
                to={row.to}
                className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:border-rose-200 hover:shadow-md transition"
              >
                <div className="text-sm font-extrabold text-gray-900">{row.title}</div>
                <div className="text-xs text-gray-500 mt-0.5">{row.hint}</div>
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <>
          <PackingStepper
            phase={phase}
            onGo={(n) => setPhase(n)}
            can2={step1Ready}
            can3={step2Ready}
          />
          {issueName ? (
            <div className="rounded-xl px-4 py-3 text-sm font-bold border" style={{ backgroundColor: '#fff1f2', color: '#9f1016', borderColor: 'rgba(196,21,28,0.2)' }}>
              Packing this issue only: {issueName} · {loading ? 'loading queue…' : `${items.length} recipient${items.length === 1 ? '' : 's'}`}
            </div>
          ) : (
            <div className="rounded-xl px-4 py-3 text-sm font-semibold border border-amber-200 bg-amber-50 text-amber-900">
              Select a magazine issue before generating or printing. Wrong issue = wrong magazine in the post.
            </div>
          )}

          {phase === 1 ? (
      <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
        <div className="text-sm font-extrabold text-gray-900 mb-1">Step 1 — Select magazine issue</div>
        <p className="text-xs text-gray-500 mb-4">Choose the month, generate the queue, then continue. Do not print another issue in this run.</p>
        {loading && issueId ? (
          <div className="mb-3 text-xs font-semibold text-gray-500">Loading recipients for this issue only…</div>
        ) : null}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
          <div className="lg:col-span-5">
            <label className="text-xs font-semibold text-gray-600">Magazine issue</label>
            <select
              className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-3 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white font-semibold"
              value={issueId}
              onChange={(e) => setIssueId(e.target.value)}
            >
              <option value="">Select issue…</option>
              {issues.map((iss) => (
                <option key={iss.id} value={iss.id}>
                  {iss.title} {iss.month_year ? `(${iss.month_year})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="lg:col-span-7 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy || !issueId}
              onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
            >
              Reload queue
            </button>
            <button
              type="button"
              disabled={busy || !issueId}
              onClick={async () => {
                if (!window.confirm(`Generate packing queue for:\n\n${issueName || 'selected issue'}\n\nOnly this magazine will be packed.`)) return;
                setBusy(true);
                try {
                  const { data } = await dispatchApi.generateFreeBatch(Number(issueId));
                  toast.success(data.message || 'Queue generated');
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.message || e.message);
                } finally {
                  setBusy(false);
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-bold shadow disabled:opacity-50"
              style={{ backgroundColor: FLARE_RED }}
            >
              Generate complimentary + entitled paid
            </button>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            disabled={!step1Ready}
            onClick={goPrint}
            className="px-5 py-2.5 rounded-lg text-white text-sm font-bold shadow disabled:opacity-50"
            style={{ backgroundColor: FLARE_RED }}
          >
            Continue to Step 2 — Print
          </button>
        </div>
      </div>
          ) : null}

          {phase === 2 ? (
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="text-sm font-extrabold text-gray-900">Step 2 — Print slips</div>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">Print only this issue. Then continue to delivery.</p>
              <div className="flex flex-wrap gap-2 items-center">
                <button type="button" disabled={busy || !items.length} onClick={toggleAll} className="px-3 py-1.5 text-sm font-semibold border border-gray-200 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50">
                  {selected.size === items.length && items.length ? 'Unselect all' : 'Select all'}
                </button>
                <button
                  type="button"
                  disabled={busy || !selectedIds.length}
                  className="px-3 py-1.5 text-sm text-white rounded-lg font-bold shadow disabled:opacity-50"
                  style={{ backgroundColor: FLARE_RED }}
                  onClick={() =>
                    runAction(async () => {
                      const { data } = await dispatchApi.printSlips(selectedIds);
                      setPreview(data.slips || []);
                      setPrintedSession(true);
                    }, 'Slip preview ready', { reload: false, keepSelection: true })
                  }
                >
                  Print slips
                </button>
                <button
                  type="button"
                  disabled={!step2Ready}
                  onClick={goDelivery}
                  className="ml-auto px-4 py-1.5 text-sm font-bold rounded-lg border border-gray-200 bg-white disabled:opacity-50"
                >
                  Continue to Step 3 — Delivery
                </button>
              </div>
            </div>
          ) : null}

          {phase === 3 ? (
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="text-sm font-extrabold text-gray-900">Step 3 — Delivery</div>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">Mark selected slips shipped. TCS tracking is optional — fill it if staff wrote a CN on the slip.</p>
              <div className="flex flex-wrap gap-2 items-center">
                <button type="button" disabled={busy || !items.length} onClick={toggleAll} className="px-3 py-1.5 text-sm font-semibold border border-gray-200 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50">
                  {selected.size === items.length && items.length ? 'Unselect all' : 'Select all'}
                </button>
                <button
                  type="button"
                  disabled={busy || !selectedIds.length}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg font-semibold bg-white hover:bg-gray-50 disabled:opacity-50"
                  onClick={() =>
                    runAction(async () => {
                      const { data } = await dispatchApi.printSlips(selectedIds);
                      setPreview(data.slips || []);
                      setPrintedSession(true);
                    }, 'Slip preview ready', { reload: false, keepSelection: true })
                  }
                >
                  Print slips
                </button>
                <input
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 min-w-[200px]"
                  placeholder="TCS tracking (optional)"
                  value={manualTracking}
                  onChange={(e) => setManualTracking(e.target.value)}
                />
                <button
                  type="button"
                  disabled={busy}
                  className="px-3 py-1.5 text-sm text-white rounded-lg font-bold shadow disabled:opacity-50"
                  style={{ backgroundColor: FLARE_RED }}
                  onClick={() => {
                    const tracking = manualTracking.trim();
                    runAction(async () => {
                      const { data } = await dispatchApi.ship({
                        item_ids: selectedIds,
                        tracking_number: tracking || null,
                        use_tcs: false,
                      });
                      const fails = (data.results || []).filter((r) => !r.ok);
                      if (fails.length) toast.warn(`${fails.length} item(s) need retry`);
                    }, tracking ? 'Marked shipped with tracking' : 'Marked shipped');
                  }}
                >
                  Mark shipped
                </button>
                <button
                  type="button"
                  disabled={busy}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg font-semibold bg-white hover:bg-gray-50 disabled:opacity-50"
                  onClick={() =>
                    runAction(async () => {
                      const { data } = await dispatchApi.sendEmails(selectedIds);
                      const skippedNoEmail = (data.skipped || []).filter((s) => s.reason === 'No email').length;
                      toast.info(`Emails sent: ${data.sent || 0}${skippedNoEmail ? ` · skipped no email: ${skippedNoEmail}` : ''}`);
                    })
                  }
                >
                  Send dispatch emails
                </button>
                <button
                  type="button"
                  onClick={finishRun}
                  className="ml-auto px-4 py-1.5 text-sm font-bold rounded-lg text-white shadow"
                  style={{ backgroundColor: '#059669' }}
                >
                  Finish packing
                </button>
              </div>
            </div>
          ) : null}

          {phase === 2 || phase === 3 ? (
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-gray-900">Packing queue</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {loading ? 'Updating…' : `${items.length} ${items.length === 1 ? 'slip' : 'slips'} in this view`}
            </div>
          </div>
        </div>
        {loading ? (
          <LoadingState label="Loading packing queue" />
        ) : loadError && items.length === 0 ? (
          <EmptyState title="Could not load packing queue" hint={loadError}>
            <button
              type="button"
              onClick={load}
              className="px-6 py-2.5 rounded-lg text-white font-bold shadow"
              style={{ backgroundColor: FLARE_RED }}
            >
              Try again
            </button>
          </EmptyState>
        ) : items.length === 0 ? (
          <EmptyState
            title={issueId ? 'Queue is empty' : 'Select a magazine issue'}
            hint={issueId
              ? 'Generate complimentary copies plus paid plans that still have this issue pending. Single-issue website orders pack from Website Orders. Inactive never enter this queue.'
              : 'Step 1: choose the issue, then Reload queue (or Generate).'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 w-10 text-left font-semibold"></th>
                  <th className="px-4 py-3 text-left font-semibold">Type</th>
                  <th className="px-4 py-3 text-left font-semibold">Recipient</th>
                  <th className="px-4 py-3 text-left font-semibold">City / Phone</th>
                  <th className="px-4 py-3 text-left font-semibold">Reference</th>
                  <th className="px-4 py-3 text-left font-semibold">Tracking</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-right font-semibold">Send</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((row, idx) => {
                  const badge = formatLabelType(row.label_type);
                  return (
                    <tr key={row.id} className={`hover:bg-rose-50/30 ${idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'}`}>
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selected.has(row.id)} onChange={() => toggle(row.id)} />
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${LABEL_BADGE[badge] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                          {badge}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-bold text-gray-900">{row.recipient_name}</div>
                        {displayEmail(row.recipient_email) ? (
                          <div className="text-xs text-gray-500 mt-0.5">{displayEmail(row.recipient_email)}</div>
                        ) : (
                          <div className="text-xs text-gray-400 mt-0.5">No email</div>
                        )}
                        {row.recipient_address ? <div className="text-xs text-gray-400 max-w-xs truncate mt-0.5">{row.recipient_address}</div> : null}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.recipient_city || '—'}
                        {row.recipient_phone ? <div className="text-xs text-gray-500 mt-0.5">Mobile {formatSlipMobile(row.recipient_phone)}</div> : null}
                        {row.recipient_landline ? <div className="text-xs text-gray-500 mt-0.5">PTCL {row.recipient_landline}</div> : null}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{row.reference}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{row.tracking_number || '—'}</td>
                      <td className="px-4 py-3">
                        <StatusPill status={row.status} />
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          disabled={busy || (phase !== 2 && phase !== 3)}
                          onClick={() => printOne(row.id)}
                          className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                          Print this
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
          ) : null}
        </>
      )}

      <SlipPreview
        slips={preview}
        branding={app}
        onClose={() => setPreview(null)}
        onPrint={() => window.print()}
      />
    </div>
  );
}
